import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useNotifications } from "../context/notifications";

function formatRelativeTime(dateStr: string): string {
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diffSec = Math.floor((now - then) / 1000);

	if (diffSec < 60) return "just now";
	const diffMin = Math.floor(diffSec / 60);
	if (diffMin < 60) return `${diffMin}m ago`;
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return `${diffHr}h ago`;
	const diffDay = Math.floor(diffHr / 24);
	return `${diffDay}d ago`;
}

function typeIcon(type: string) {
	switch (type) {
		case "billing":
		case "payment_failed":
			return (
				<span className="text-yellow-500" title={type}>
					&#9888;
				</span>
			);
		case "success":
		case "subscription_created":
		case "plan_changed":
			return (
				<span className="text-green-500" title={type}>
					&#10003;
				</span>
			);
		default:
			return (
				<span className="text-blue-500" title={type}>
					&#9432;
				</span>
			);
	}
}

export function NotificationBell() {
	const { notifications, unreadCount, markRead, markAllRead } =
		useNotifications();
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		}
		if (open) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open]);

	async function handleNotificationClick(
		id: string,
		metadata: string | null,
		read: boolean,
	) {
		if (!read) {
			await markRead(id);
		}
		if (metadata) {
			try {
				const parsed = JSON.parse(metadata) as { link?: string };
				if (parsed.link) {
					navigate(parsed.link);
					setOpen(false);
				}
			} catch {
				// invalid JSON — ignore
			}
		}
	}

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="relative text-gray-600 hover:text-gray-900 cursor-pointer"
				aria-label="Notifications"
				data-testid="notification-bell"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className="h-5 w-5"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
					aria-label="Notifications"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
					/>
				</svg>
				{unreadCount > 0 && (
					<span
						className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
						data-testid="notification-badge"
					>
						{unreadCount > 99 ? "99+" : unreadCount}
					</span>
				)}
			</button>

			{open && (
				<div
					data-testid="notification-dropdown"
					className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
				>
					<div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
						<span className="text-sm font-semibold text-gray-900">
							Notifications
						</span>
						{unreadCount > 0 && (
							<button
								type="button"
								onClick={() => markAllRead()}
								className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
								data-testid="mark-all-read"
							>
								Mark all as read
							</button>
						)}
					</div>

					{notifications.length === 0 ? (
						<div className="px-4 py-6 text-center text-sm text-gray-400">
							No notifications
						</div>
					) : (
						<div>
							{notifications.map((n) => (
								<button
									key={n.id}
									type="button"
									onClick={() =>
										handleNotificationClick(n.id, n.metadata, n.read)
									}
									className={`w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-2 ${
										n.read
											? "border-transparent"
											: "border-blue-500 bg-blue-50/30"
									}`}
									data-testid="notification-item"
								>
									<div className="flex items-start gap-2">
										<div className="mt-0.5">{typeIcon(n.type)}</div>
										<div className="flex-1 min-w-0">
											<div className="flex items-center justify-between gap-2">
												<span
													className={`text-sm truncate ${n.read ? "text-gray-700" : "font-semibold text-gray-900"}`}
												>
													{n.title}
												</span>
												<span className="text-xs text-gray-400 whitespace-nowrap">
													{formatRelativeTime(n.createdAt)}
												</span>
											</div>
											<p className="text-xs text-gray-500 truncate mt-0.5">
												{n.body}
											</p>
										</div>
									</div>
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
